import {
  ColDef,
  Column,
  CsvExportParams,
  GridOptions,
  ProcessCellForExportParams,
  ValueFormatterParams,
} from "ag-grid-community/main";
import { moment } from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import { ShouldRowBeSkippedParams } from "ag-grid-community/dist/lib/exporter/exportParams";
const logger = LoggerService.for(__dirname);

/**
 * Setting for exporting data - only one property is mandatory - exportToFile - the rest
 * have sensible defaults that can be overrideen if required
 */
export interface BlotterCSVExportSettings {
  /**
   * Should the CSV output be sent to file? If true, a file name is created using the specified file name, or
   * date and file name base, and immediately downloaded into the browser from ag-grid.
   * If false, the export returns the string representation ofthe CSV
   */
  exportToFile: boolean;
  /**
   * Filter that returns true for all columns to be inclue in the outout - will be called for all defined columns.
   * All columns are output by default.
   */
  columnFilter?: (col: Column) => boolean;
  /** Filter that returns true if a grid row should be removed from the CSV output. All rows
   * are output by default
   */
  rowFilter?: (rowParams: ShouldRowBeSkippedParams) => boolean;
  /**
   * If exportToFile is true, then specify a potental output file name. If not defined, the file base is used
   */
  fileName?: string;
  /**
   * If exportToFile is true and no fileName specified, then specify a suffix added to a timestamp
   * as the file name
   */
  fileBase?: string;
  /**
   * Fomatter function for changing the name of the column header, if required
   * @param col
   * @returns
   */
  headerFormatter?: (col: ColDef) => string;
  /**
   * formatter function for changing the cell output - if not specified then the formatted value is used
   * @param valueParams
   * @returns
   */
  cellFormatter?: (valueParams: ProcessCellForExportParams) => string;
}

/**
 * Manage the transformation of grid data to CSV output.
 */
export class CSVExportHelper {
  constructor() {
    //
  }

  /**
   * Manage the transformation of grid data from a single blotter to CSV output. If exportToFile is set, then the entire CSV
   * creation and export is managed, otherwise the CSV is created as a string and resolved in the returned promise
   *
   * @param gridOptions
   * @param exportSettings
   * @returns
   */
  public exportToCSV(
    gridOptions: GridOptions,
    exportSettings: BlotterCSVExportSettings,
  ): Promise<string | null> {
    if (!gridOptions || !exportSettings || !gridOptions.api) {
      logger.out.warn(`${this.constructor.name}: invalid export settings`);
      return Promise.reject("invalidation export parameters");
    }

    // Determine a filename if required
    let filename: string;
    if (exportSettings.exportToFile) {
      if (exportSettings.fileName) {
        filename = exportSettings.fileName;
      } else {
        const now = new Date();
        const dateStr = moment(now).format("YYYY-MM-DD_HH-mm-ss");
        filename = dateStr + "_" + (exportSettings.fileBase ?? "export") + ".csv";
      }
    }

    // Get all the column keys for exporting
    let allColumns: Column[] = gridOptions.columnApi?.getAllGridColumns();
    let allColumnKeys: string[];
    if (allColumns.length > 0) {
      allColumnKeys = this.filterExportColumnIds(allColumns, exportSettings);
    } else {
      logger.out.warn(`${this.constructor.name}: no columns for export found`);
      return Promise.reject("No columns found");
    }

    // Callback for resolving header names - will default to defined header name
    const processHeaderCallback = exportSettings.headerFormatter
      ? (param: ValueFormatterParams) => exportSettings.headerFormatter(param.column.getColDef())
      : (param: ValueFormatterParams) => param.column.getColDef().headerName;

    // Callback for filtering rows - default is to include all rows
    const shouldRowBeSkipped = exportSettings.rowFilter
      ? (param: ShouldRowBeSkippedParams) => exportSettings.rowFilter(param)
      : (_param: ShouldRowBeSkippedParams) => false;

    // Callback for formatting values - default is to use colyumn formatter for all cells
    const processCellCallback = exportSettings.cellFormatter
      ? (param: ProcessCellForExportParams) => exportSettings.cellFormatter(param)
      : CSVExportHelper.cellFormatter;
    const params: CsvExportParams = {
      skipHeader: false,
      columnGroups: false,
      skipFooters: true,
      skipGroups: true,
      skipPinnedTop: true,
      skipPinnedBottom: true,
      columnKeys: allColumnKeys,
      onlySelected: false,
      suppressQuotes: false,
      fileName: filename,
      processHeaderCallback: processHeaderCallback,
      shouldRowBeSkipped: shouldRowBeSkipped,
      processCellCallback: processCellCallback,
    };

    return new Promise((resolve, reject) => {
      if (exportSettings.exportToFile) {
        gridOptions.api.exportDataAsCsv(params);
        resolve(null);
      } else {
        let dataAsCsv = gridOptions.api.getDataAsCsv(params);
        resolve(dataAsCsv);
      }
    });
  }

  /**
   * Static function to get the correctly formatted value from an export params instance. Uses the
   * formatter from the column to return the exact value displayed on the blotter
   * @param param
   * @returns
   */
  public static cellFormatter(param: ProcessCellForExportParams) {
    const data = param.node.data;
    const correctParam = {
      ...param,
      data,
      colDef: param.column.getColDef(),
    } as ValueFormatterParams;
    const fallback = typeof param.value === "boolean"
      ? (param.value ? "Y" : "N")
      : (param.value ? param.value.toString() : "");
    return CSVExportHelper.formatExportValue(correctParam, fallback);
  }

  public static formatExportValue(
    param: ValueFormatterParams,
    fallback: any,
    emptyValue: any = "",
  ): any {
    const formatter = param.column.getColDef()?.valueFormatter;
    const value = formatter ? formatter(param) : fallback;
    return value == null ? emptyValue : value;
  }

  /**
   * Get the list of column Ids to export
   * @param allColumns
   * @param exportSettings
   * @returns
   */
  private filterExportColumnIds(allColumns: Column[], exportSettings: BlotterCSVExportSettings) {
    let filteredCols: Column[];
    if (exportSettings.columnFilter) {
      filteredCols = allColumns.filter(exportSettings.columnFilter);
    } else {
      filteredCols = allColumns.filter((col) => {
        return true;
      });
    }
    return filteredCols.map((col) => col.getColDef().colId);
  }
}
